import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../app/Api/ApiSlice";
import { toast } from "react-toastify";
import "./storesStyles.css";

const Invoice = () => {
    const [invoice, setInvoice] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const { id } = useParams();

    useEffect(() => {
        const getInvoice = async () => {
            try {
                setIsLoading(true);
                const response = await api.get(`/stores/invoices/${id}`);
                // console.log(response)
                setInvoice(response.data || null);
            } catch (error) {
                toast.error(
                    error?.response?.data?.message || "Failed to load invoice"
                );
            } finally {
                setIsLoading(false);
            }
        };

        if (id) getInvoice();
    }, [id]);

    const handleDownload = async () => {

        try {
            const res = await api.get(
                `stores/${invoice.store.name}/invoices/${invoice._id}/download`,
                {
                    responseType: "blob", // critical for PDF
                }
            );

            // Create a URL for the file
            const url = window.URL.createObjectURL(new Blob([res.data]));

            // Create a temporary link
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `invoice-${invoice._id}.pdf`);

            // Append, trigger download, then clean up
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    /* ========================
       Calculations
    ======================== */
    const subtotal = useMemo(() => {
        if (!invoice?.items) return 0;
        return invoice.items.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0
        );
    }, [invoice]);

    const tax = invoice?.tax || 0;
    const discount = invoice?.discount || 0;
    const grandTotal = subtotal + tax - discount;

    /* ========================
       Loading & Empty States
    ======================== */
    if (isLoading) return <p>Loading invoice...</p>;

    if (!invoice)
        return <p className="not-found-msg">Invoice not found</p>;

    return (
        <div className="store-orders-container">
            <h2 className="page-title">Invoice</h2>

            {/* ========================
         Invoice Summary (same as order-details)
      ======================== */}
            <div className="order-details">
                <div className="order-details-info">
                    <strong>Invoice No:</strong>
                    <span>{invoice.invoiceNo}</span>
                </div>

                <div className="order-details-info">
                    <strong>Status:</strong>
                    <span
                        className={`order-status ${invoice.isPaid ? "Paid" : "Pending"
                            }`}
                    >
                        {invoice.isPaid ? "Paid" : "Due"}
                    </span>
                </div>

                <div className="order-details-info">
                    <strong>Order ID:</strong>
                    <span>{invoice.order}</span>
                </div>

                <div className="order-details-info">
                    <strong>Issued On:</strong>
                    <span>
                        {new Date(invoice.createdAt).toLocaleDateString()}
                    </span>
                </div>

                {!invoice.isPaid && invoice.dueDate && (
                    <div className="order-details-info">
                        <strong>Due Date:</strong>
                        <span>
                            {new Date(invoice.dueDate).toLocaleDateString()}
                        </span>
                    </div>
                )}
            </div>

            {/* ========================
         Seller Info (consumer-facing)
      ======================== */}
            <div className="order-details">
                <div className="order-details-info">
                    <strong>Seller:</strong>
                    <span>{invoice.store?.name}</span>
                </div>

                <div className="order-details-info">
                    <strong>Address:</strong>
                    <span>
                        {invoice.store?.address?.city},{" "}
                        {invoice.store?.address?.country}
                    </span>
                </div>

                <div className="order-details-info">
                    <strong>Phone:</strong>
                    <span>{invoice.store?.contacts?.phone}</span>
                </div>

                <div className="order-details-info">
                    <strong>Email:</strong>
                    <span>{invoice.store?.contacts?.email}</span>
                </div>

                <div className="order-details-info">
                    <strong>Tax No:</strong>
                    <span>{invoice.store?.taxNo}</span>
                </div>

                <div className="order-details-info">
                    <strong>Website:</strong>
                    <span>
                        {invoice.store?.website ||
                            `www.onnize.com/b/${invoice.store?.name}`}
                    </span>
                </div>
            </div>

            {/* ========================
         Invoice Items Table (same as order table)
      ======================== */}
            <div className="table-wrapper">
                <table className="orders-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Price</th>
                            <th>Qty</th>
                            <th>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice.items?.map(item => (
                            <tr key={item._id}>
                                <td>{item.name}</td>
                                <td>${item.price}</td>
                                <td>{item.quantity}</td>
                                <td className="order-total">
                                    ${item.price * item.quantity}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ========================
         Price Breakdown (same classnames)
      ======================== */}
            <div className="price-breakdown">
                <div className="price-row">
                    <span>Subtotal</span>
                    <span>${subtotal}</span>
                </div>

                <div className="price-row">
                    <span>Tax</span>
                    <span>${tax}</span>
                </div>

                {discount > 0 && (
                    <div className="price-row discount">
                        <span>Discount</span>
                        <span>- ${discount}</span>
                    </div>
                )}

                <div className="price-row grand-total">
                    <strong>Total</strong>
                    <strong>${grandTotal}</strong>
                </div>
            </div>

            {/* ========================
         Invoice Actions (consumer)
      ======================== */}
            <div className="order-actions">
                {!invoice.isPaid && (
                    <button className="btn-primary">
                        Pay Invoice
                    </button>
                )}

                <button className="btn-secondary" onClick={() => handleDownload()}>
                    Download PDF
                </button>
            </div>

            <Link to={`/orders/${invoice.order}`} className="back-link">
                ← Back to order
            </Link>
        </div>
    );
};

export default Invoice;
